import { z } from "zod";
import { IdSchema, IsoDateTimeSchema } from "./common.js";

export const GitMirrorStatusSchema = z.enum(["NONE", "SYNCED", "ERROR"]);
export type GitMirrorStatus = z.infer<typeof GitMirrorStatusSchema>;

export const GitRepositorySchema = z.object({
	id: IdSchema,
	name: z.string().min(1),
	cloneUrl: z.string().url(),
	defaultBranch: z.string().min(1),
	mirrorStatus: GitMirrorStatusSchema,
});
export type GitRepository = z.infer<typeof GitRepositorySchema>;

export const IssueGitCredentialRequestSchema = z.object({
	permission: z.enum(["READ", "WRITE"]).default("READ"),
	ttlSeconds: z.number().int().min(1).max(86_400).default(3600),
});
export type IssueGitCredentialRequest = z.infer<typeof IssueGitCredentialRequestSchema>;

export const IssueGitCredentialResponseSchema = z.object({
	username: z.string().min(1),
	credential: z.string().min(1),
	cloneUrl: z.string().url(),
	expiresAt: IsoDateTimeSchema,
});
export type IssueGitCredentialResponse = z.infer<typeof IssueGitCredentialResponseSchema>;

export const CreateGitPullRequestRequestSchema = z.object({
	title: z.string().min(1).max(255),
	body: z.string().max(65_535),
	head: z.string().min(1).max(255),
	base: z.string().min(1).max(255),
});
export type CreateGitPullRequestRequest = z.infer<typeof CreateGitPullRequestRequestSchema>;

export const CreateGitPullRequestResponseSchema = z.object({
	number: z.number().int().positive(),
	url: z.string().url(),
});
export type CreateGitPullRequestResponse = z.infer<typeof CreateGitPullRequestResponseSchema>;

export const GitPullRequestSchema = z.object({
	number: z.number().int().positive(),
	title: z.string().min(1),
	state: z.string().min(1),
	url: z.string().url(),
});
export type GitPullRequest = z.infer<typeof GitPullRequestSchema>;

export const PrepareGitWorkspaceRequestSchema = z.object({
	runtimeId: IdSchema,
});

export const PrepareGitWorkspaceResponseSchema = z.object({
	repositoryId: IdSchema,
	runtimeId: IdSchema,
	operation: z.enum(["CLONED", "PULLED"]),
});
export type PrepareGitWorkspaceResponse = z.infer<typeof PrepareGitWorkspaceResponseSchema>;

export const FinalizeGitWorkspaceRequestSchema = z.object({
	runtimeId: IdSchema,
	sessionId: IdSchema,
	branch: z.string().min(1).max(255),
	message: z.string().min(1).max(4096),
	expectedBase: z.string().min(1).max(255),
});
export type FinalizeGitWorkspaceRequest = z.infer<typeof FinalizeGitWorkspaceRequestSchema>;

export const GitWorkspaceDiffSummaryEntrySchema = z.object({
	path: z.string().min(1),
	additions: z.number().int().min(0),
	deletions: z.number().int().min(0),
});

export const FinalizeGitWorkspaceResponseSchema = z.object({
	commitSha: z.string().regex(/^[0-9a-f]{40}$/),
	diffSummary: z.array(GitWorkspaceDiffSummaryEntrySchema),
});
export type FinalizeGitWorkspaceResponse = z.infer<typeof FinalizeGitWorkspaceResponseSchema>;

/**
 * Ensure a private Gitea repository exists for a Project (workspace).
 * Idempotent: returns the existing mapped repo when one is already bound.
 */
export const EnsureGitRepositoryRequestSchema = z.object({
	/** Optional override. Default: `ws-{projectId}` (sanitized). */
	name: z
		.string()
		.min(1)
		.max(100)
		.regex(/^[a-zA-Z0-9._-]+$/, "must be a Gitea-safe repository name")
		.optional(),
	/** Default branch for newly created repos. Existing repos keep their branch. */
	defaultBranch: z.string().min(1).max(255).default("main"),
});
export type EnsureGitRepositoryRequest = z.infer<typeof EnsureGitRepositoryRequestSchema>;

export const EnsureGitRepositoryResponseSchema = GitRepositorySchema.extend({
	/** true when this call created the Gitea repository (not merely remapped). */
	created: z.boolean(),
});
export type EnsureGitRepositoryResponse = z.infer<typeof EnsureGitRepositoryResponseSchema>;
